import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import MainLayout from "../../components/MainLayout";
import event from "../../services/event";
import queryDefault from "../../utils/queryDefault";
import { rupiah } from "../../utils/currency";
import { Helmet } from "react-helmet-async";
import Breadcrumb from "../../components/Breadcrumb";
import { grey } from "@mui/material/colors";

const isDatePassed = (dateString: string) => {
  const currentDate = new Date();
  const inputDate = new Date(dateString);
  return inputDate < currentDate;
};

export default function Home() {
  const [options, setOptions] = useState<any>({
    keyword: "",
  });

  const listEvent = useQuery(
    ["events", options],
    ({ queryKey }) => {
      return event.getAllPublicEvent({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const navigate = useNavigate();

  // Loading and error states
  if (listEvent.isLoading) {
    return (
      <MainLayout>
        <Box px={1} py={4} textAlign="center">
          <Typography variant="h6">Loading events...</Typography>
        </Box>
      </MainLayout>
    );
  }

  if (listEvent.isError) {
    return (
      <MainLayout>
        <Box px={1} py={4} textAlign="center">
          <Typography variant="h6" color="error">Failed to load events. Please try again later.</Typography>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Helmet>Home</Helmet>
      <Breadcrumb
        label="SODTix Upcoming Events"
        breadcrumbs={[]}
      />
      <Box px={1}>
        <Grid container spacing={3}>
          {listEvent.data?.data?.map(
            (event: any) =>
              event.show && !isDatePassed(event.end_date) && (
                <Grid item xs={6} sm={6} md={4} lg={3} key={event.id}>
                  <Box
                    component={RouterLink}
                    to={`/event/${event.slug}`}
                    tabIndex={0}
                    sx={{
                      display: 'block',
                      borderRadius: 3,
                      boxShadow: 3,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      textDecoration: 'none',
                      color: 'inherit',
                      position: 'relative',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 6,
                      },
                      '&:focus-visible': {
                        outline: '2px solid #1976d2',
                        outlineOffset: '2px',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <Card
                      sx={{
                        cursor: "pointer",
                        borderRadius: 3,
                        boxShadow: 0,
                        transition: "none",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <CardMedia
                        component="img"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          aspectRatio: '217/89', // Using intrinsic aspect ratio
                          objectFit: 'contain', // Changed to contain to show full image
                          backgroundColor: '#f5f5f5' // Light background in case of letterboxing
                        }}
                        image={process.env.REACT_APP_BACKEND_URL + event.photo}
                        alt={event.name}
                        loading="lazy"
                      />
                      <CardContent>
                        <Typography
                          style={{ fontSize: "2vh" }}
                          variant="h6"
                          fontWeight="bold"
                          noWrap
                        >
                          {event.name}
                        </Typography>
                        <Typography
                          style={{ fontSize: "2vh" }}
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {event.location}
                        </Typography>
                        <Typography
                          style={{ fontSize: "0.6rem" }}
                          variant="subtitle1"
                          color="primary"
                          mt={1}
                        >
                          From {rupiah(event.starting_price)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              )
          )}
        </Grid>
        <Box sx={{ mt: 3, mb: 2, py: 2, width: '100%', bgcolor: grey[200], borderRadius: 5, textAlign: 'center' }}>
          <Typography
            variant="h5"
            fontWeight="bold">
            Past Events
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {listEvent.data?.data?.map(
            (event: any) =>
              event.show && isDatePassed(event.end_date) && (
                <Grid item xs={6} sm={6} md={4} lg={3} key={event.id}>
                  <Box
                    component={RouterLink}
                    to={`/event/${event.slug}`}
                    tabIndex={0}
                    sx={{
                      display: 'block',
                      borderRadius: 3,
                      boxShadow: 3,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      textDecoration: 'none',
                      color: 'inherit',
                      position: 'relative',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 6,
                      },
                      '&:focus-visible': {
                        outline: '2px solid #1976d2',
                        outlineOffset: '2px',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <Card
                      sx={{
                        cursor: "pointer",
                        borderRadius: 3,
                        boxShadow: 0,
                        transition: "none",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <CardMedia
                        component="img"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          aspectRatio: '217/89', // Using intrinsic aspect ratio
                          objectFit: 'contain', // Changed to contain to show full image
                          backgroundColor: '#f5f5f5' // Light background in case of letterboxing
                        }}
                        image={process.env.REACT_APP_BACKEND_URL + event.photo}
                        alt={event.name}
                        loading="lazy"
                      />
                      <CardContent>
                        <Typography
                          style={{ fontSize: "2vh" }}
                          variant="h6"
                          fontWeight="bold"
                          noWrap
                        >
                          {event.name}
                        </Typography>
                        <Typography
                          style={{ fontSize: "2vh" }}
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {event.location}
                        </Typography>
                        <Typography
                          style={{ fontSize: "0.6rem" }}
                          variant="subtitle1"
                          color="primary"
                          mt={1}
                        >
                          From {rupiah(event.starting_price)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              )
          )}
        </Grid>
      </Box>
    </MainLayout>
  );
}
